import React from 'react';
import { useAppSelector } from 'hooks';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { weeklyPlannerDayDisplayFormat } from '~libs/dayjs';
import { UnallocatedAppointment } from '~weekly-planner/lib/common';

import Button from '~components/Button';
import Icon, { faArrowRight, faRepeat } from '~components/icon';
import { WorkerQualifications } from '~weekly-planner/components/common';

import { selectFocusedAppointment, selectOptions as selectWeeklyPlannerOptions } from '~weekly-planner/selectors';
import { selectChangeById } from '~weekly-planner/selectors/appointments';

import StatusStrip from './StatusStrip';

interface ComponentProps {
  appointment: UnallocatedAppointment;
  isActionMenuOpen?: boolean;
  anyActionMenuOpen?: boolean;
}

const UnallocatedCard: React.FC<ComponentProps> = ({ appointment, isActionMenuOpen, anyActionMenuOpen }) => {
  const { unallocatedDuration, unallocatedQualifications, unallocatedServiceType, unallocatedSuburb } =
    useAppSelector(selectWeeklyPlannerOptions);

  const focused = useAppSelector(selectFocusedAppointment);

  const { appointment: focusedAppointment } = focused || {};
  const { key: focusKey } = focusedAppointment || {};

  const {
    key,
    id,
    client,
    date,
    preferred_user,
    parent_repeat_id,
    start_time,
    end_time,
    duration,
    service_type,
    status,
    qualification_level,
    is_bidding,
    bidding_user_ids,
  } = appointment;
  const { full_name, suburb } = client;

  const hasChanged = useAppSelector((state) => selectChangeById(state, Number(key)));

  const renderPreference = () => {
    let displayPreference = 'Appt has no worker preference';
    const classes = [];
    if (id === null && preferred_user) {
      const { full_name, is_available, is_qualified } = preferred_user;
      displayPreference = `Worker preference: ${full_name}`;

      if (!is_available) {
        displayPreference += ' (NA)';
        classes.push('not-available');
      }
      if (!is_qualified) {
        displayPreference += ' (NQ)';
        classes.push('not-qualified');
      }
    }

    const className = classNames('preference', classes);
    return <div className={className}>{displayPreference}</div>;
  };

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    let appointmentId = id;
    if (id === null && parent_repeat_id) appointmentId = parent_repeat_id;

    window.open(`/appointments/${appointmentId}`, '_blank', 'noopener,noreferrer');
  };

  const cardClass = classNames('unallocated-card', {
    'greyed-out': (anyActionMenuOpen && !isActionMenuOpen) || (focusKey && focusKey !== key),
  });

  const appointmentClass = classNames('appointment-content', {
    'hide-address': !unallocatedSuburb,
  });

  const biddingClass = classNames('bidding', {
    rejected: is_bidding && bidding_user_ids.length === 0,
  });

  return (
    <div className={cardClass}>
      <div className="card-row">
        <StatusStrip status={status} changed={hasChanged} />
        <div className="content">
          <div className={appointmentClass}>
            <div className="client">
              <div>{full_name}</div>
              {unallocatedSuburb && <div>{suburb}</div>}
              {unallocatedServiceType && <div className="service">{service_type?.name || ''}</div>}
            </div>
            <div className="date-time">
              <div>{dayjs(date).format(weeklyPlannerDayDisplayFormat)}</div>
              <div className="start-end">
                {start_time} - {end_time}
              </div>
              {unallocatedDuration && <div className="duration">{duration} mins</div>}
            </div>
          </div>

          {unallocatedQualifications && (
            <div className="qualifications">
              <WorkerQualifications qualifications={qualification_level} />
            </div>
          )}
          <div className="extras">
            {renderPreference()}
            <div className="d-flex">
              <Button
                className="hover-button"
                color="link"
                size="sm"
                onClick={handleViewClick}
                draggable="false"
                onPointerDown={(event) => event.stopPropagation()}
              >
                {id === null && parent_repeat_id ? (
                  <Icon icon={faRepeat} title="Repeat Appointment" />
                ) : (
                  <Icon icon={faArrowRight} title="Single Appointment" />
                )}
                <span className="hover-text">View</span>
              </Button>
            </div>
          </div>
          {is_bidding && <div className={biddingClass}>{`Bidding (${bidding_user_ids.length} CW)`}</div>}
        </div>
      </div>
    </div>
  );
};

export default UnallocatedCard;
